import { motion } from 'framer-motion';
import { Award, CheckCircle } from 'lucide-react';

export default function Services() {
  const highlightedService = {
    title: 'BPS Denture',
    subtitle: 'Certified BPS Denture Specialist',
    description: 'Dr. Chandrashekhar Harale is a Certified BPS Clinician with a Certified BPS Denture Lab and over 10+ years of clinical experience in BPS Denture solutions. BPS (Biofunctional Prosthetic System) dentures are premium, precision-crafted dentures that provide superior comfort, aesthetics, and functionality.',
    features: [
      'Perfect fit with minimal adjustments',
      'Natural appearance and smile',
      'Enhanced chewing efficiency',
      'Long-lasting durability',
      'Biocompatible materials',
      'Customized to your facial structure'
    ],
    image: 'https://images.unsplash.com/photo-1738992696295-ce6b85532ca9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGluZGlhbiUyMGZhbWlseSUyMHNtaWxpbmclMjBoZWFsdGh5JTIwdGVldGglMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njg3NzQ3NjB8MA&ixlib=rb-4.1.0&q=85'
  };

  const services = [
    {
      title: 'Dental Implant',
      description: 'Permanent solution for missing teeth using titanium implants that fuse with your jawbone, providing a stable foundation for replacement teeth.',
      icon: 'ðŸ¦·'
    },
    {
      title: 'Root Canal Treatment',
      description: 'Advanced, painless root canal procedures to save infected teeth using modern techniques and equipment for comfortable treatment.',
      icon: 'ðŸ’Š'
    },
    {
      title: 'Dental Laminates',
      description: 'Transform your smile with ultra-thin, custom-made shells that cover the front surface of teeth for a perfect, natural-looking smile.',
      icon: 'âœ¨'
    },
    {
      title: 'Tooth Polishing',
      description: 'Professional cleaning and polishing to remove stains and plaque, leaving your teeth bright and healthy.',
      icon: 'ðŸ’Ž'
    },
    {
      title: 'Dental Consultation',
      description: 'Comprehensive oral health evaluation and personalized treatment planning for all your dental needs.',
      icon: 'ðŸ©º'
    },
    {
      title: 'Impacted Tooth Extraction',
      description: 'Safe and comfortable extraction of impacted wisdom teeth and other problematic teeth with minimal discomfort.',
      icon: 'ðŸ”§'
    },
    {
      title: 'Dental Inlays and Onlays',
      description: 'Custom-fitted restorations that repair damaged teeth while preserving natural tooth structure.',
      icon: 'ðŸŽ¯'
    },
    {
      title: 'Oral Rehabilitation',
      description: 'Complete restoration of oral function and aesthetics for patients with extensive dental problems.',
      icon: 'â™»ï¸'
    },
    {
      title: 'Surgical Extraction',
      description: 'Expert surgical procedures for complex tooth extractions with advanced techniques for faster healing.',
      icon: 'âš•ï¸'
    },
    {
      title: 'Crown & Bridge',
      description: 'Custom dental crowns and bridges to restore damaged teeth and replace missing ones with natural-looking results.',
      icon: 'ðŸ‘‘'
    },
    {
      title: 'Smile Design',
      description: 'Comprehensive cosmetic dentistry to create your dream smile through a combination of treatments.',
      icon: 'ðŸ˜Š'
    },
    {
      title: 'Preventive Care',
      description: 'Regular check-ups, cleanings, and preventive treatments to maintain optimal oral health.',
      icon: 'ðŸ›¡ï¸'
    }
  ];

  return (
    <div data-testid="services-page">
      <section className="pt-32 pb-20 bg-gradient-to-r from-blue-50 to-cyan-50" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-bold text-primary uppercase tracking-widest">Our Services</span>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl text-slate-900 mt-4 mb-6">
              Complete Dental Care for All Your Needs
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From routine check-ups to advanced dental procedures, we offer comprehensive care with expertise and compassion
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary to-secondary text-white" data-testid="bps-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <Award className="w-5 h-5" />
                <span className="font-semibold">Certified Specialist</span>
              </div>
              
              <h2 className="font-heading font-bold text-4xl sm:text-5xl mb-6">
                {highlightedService.title}
              </h2>
              <p className="text-xl mb-4 text-white/90">{highlightedService.subtitle}</p>
              <p className="text-lg leading-relaxed mb-8 text-white/80">
                {highlightedService.description}
              </p>

              <div className="space-y-3">
                {highlightedService.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`bps-feature-${index}`}>
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={highlightedService.image}
                alt="BPS Denture"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="all-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl text-slate-900 mb-4">
              All Services
            </h2>
            <p className="text-xl text-slate-600">
              Comprehensive dental treatments tailored to your needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-blue-200"
                data-testid={`service-${index}`}
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="service-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1565090567208-c8038cfcf6cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMGRlbnRpc3QlMjB0cmVhdGluZyUyMHBhdGllbnQlMjBzbWlsZSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzY4Nzc0NzU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern dental care"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold text-primary uppercase tracking-widest">Why Our Services Stand Out</span>
              <h2 className="font-heading font-bold text-4xl text-slate-900 mt-4 mb-6">
                Advanced Care, Exceptional Results
              </h2>

              <div className="space-y-6">
                {[
                  { title: 'State-of-the-Art Technology', desc: 'Latest equipment and techniques for precise treatments' },
                  { title: 'Experienced Expertise', desc: '16+ years of specialized dental practice' },
                  { title: 'Comfortable Environment', desc: 'Clean, modern clinic designed for patient comfort' },
                  { title: 'Personalized Approach', desc: 'Customized treatment plans for each patient' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4" data-testid={`feature-${index}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
